import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_CONFIG } from '@/constants/apiEndpoints';
import { logger } from '@/utils/logger';
import { isOidcEnabled } from '@/auth/oidcClient';
import { isPublicInterviewRoute } from '@/utils/routeHelpers';

const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: API_CONFIG.TIMEOUT,
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let refreshSubscribers: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

// Retry configuration
const RETRY_CONFIG = {
  MAX_RETRIES: API_CONFIG.RETRY_ATTEMPTS,
  INITIAL_DELAY: API_CONFIG.RETRY_DELAY,
  MAX_DELAY: 10000, // 10 seconds
  BACKOFF_MULTIPLIER: 2,
  RETRYABLE_STATUS_CODES: [408, 429, 502, 503, 504],
};

// Function to subscribe to token refresh
const subscribeTokenRefresh = (
  resolve: (token: string) => void,
  reject: (error: unknown) => void
) => {
  refreshSubscribers.push({ resolve, reject });
};

// Function to notify all subscribers when token is refreshed
const onTokenRefreshed = (token: string) => {
  refreshSubscribers.forEach((subscriber) => subscriber.resolve(token));
  refreshSubscribers = [];
};

const onTokenRefreshFailed = (error: unknown) => {
  refreshSubscribers.forEach((subscriber) => subscriber.reject(error));
  refreshSubscribers = [];
};

// Use sessionStorage for better security (cleared when browser tab closes)
const storage = sessionStorage;

// Function to clear all auth data
const clearAuthData = () => {
  storage.removeItem('access_token');
  storage.removeItem('refresh_token');
  storage.removeItem('user');
};

// Function to refresh the access token
const refreshAccessToken = async (): Promise<string | null> => {
  if (isOidcEnabled()) {
    return null;
  }

  try {
    const refreshToken = storage.getItem('refresh_token');
    if (!refreshToken) {
      return null;
    }

    const response = await axios.post(`${API_CONFIG.BASE_URL}/v1/auth/refresh`, {
      refresh_token: refreshToken,
    });

    const { access_token, refresh_token: newRefreshToken } = response.data;

    // Store new tokens in sessionStorage
    storage.setItem('access_token', access_token);
    if (newRefreshToken) {
      storage.setItem('refresh_token', newRefreshToken);
    }

    return access_token;
  } catch (error) {
    // Refresh failed, clear tokens and redirect to login
    clearAuthData();
    return null;
  }
};

// Helper function to determine if error is retryable
const isRetryableError = (error: AxiosError): boolean => {
  // Network errors (no response)
  if (!error.response) {
    return true;
  }

  // Specific status codes that should be retried
  const status = error.response.status;
  return RETRY_CONFIG.RETRYABLE_STATUS_CODES.includes(status);
};

// Helper function to calculate retry delay with exponential backoff
const getRetryDelay = (retryCount: number, error: AxiosError): number => {
  // Check for Retry-After header (for 429 responses)
  if (error.response?.status === 429) {
    const retryAfter = error.response.headers['retry-after'];
    if (retryAfter) {
      const retryAfterMs = parseInt(retryAfter) * 1000;
      return Math.min(retryAfterMs, RETRY_CONFIG.MAX_DELAY);
    }
  }

  // Exponential backoff
  const delay = RETRY_CONFIG.INITIAL_DELAY * Math.pow(RETRY_CONFIG.BACKOFF_MULTIPLIER, retryCount);
  return Math.min(delay, RETRY_CONFIG.MAX_DELAY);
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = storage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      _retryCount?: number;
    };

    // Ensure we have a valid config
    if (!originalRequest) {
      return Promise.reject(error);
    }

    // Initialize retry count
    if (!originalRequest._retryCount) {
      originalRequest._retryCount = 0;
    }

    // Handle 401 errors with token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isPublicInterviewRoute()) {
        return Promise.reject(error);
      }

      if (isOidcEnabled()) {
        // If there's no refresh_token, the OIDC→backend exchange hasn't
        // completed yet. Don't redirect — let OidcSessionSync finish the
        // exchange and let React Query retry the request afterward.
        if (!storage.getItem('refresh_token')) {
          return Promise.reject(error);
        }
        clearAuthData();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Wait for the token to be refreshed
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh(
            (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(api(originalRequest));
            },
            (refreshError) => {
              reject(refreshError);
            }
          );
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();

        if (newToken) {
          isRefreshing = false;
          onTokenRefreshed(newToken);

          // Retry the original request with the new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } else {
          // Token refresh failed, redirect to login
          isRefreshing = false;
          onTokenRefreshFailed(error);
          window.location.href = '/login';
          return Promise.reject(error);
        }
      } catch (refreshError) {
        isRefreshing = false;
        onTokenRefreshFailed(refreshError);
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Handle retryable errors with exponential backoff (unless the request
    // opted out of retries, e.g. large media uploads).
    if (
      !originalRequest.skipRetry &&
      isRetryableError(error) &&
      originalRequest._retryCount < RETRY_CONFIG.MAX_RETRIES
    ) {
      // Do NOT retry OIDC login endpoint — retrying amplifies rate-limit pressure
      const requestUrl = originalRequest.url || '';
      if (requestUrl.includes('/auth/login/oidc')) {
        return Promise.reject(error);
      }

      originalRequest._retryCount++;

      const delay = getRetryDelay(originalRequest._retryCount - 1, error);

      logger.warn(
        `[API Retry] Retrying request (attempt ${originalRequest._retryCount}/${RETRY_CONFIG.MAX_RETRIES}) after ${delay}ms`,
        {
          url: originalRequest.url,
          method: originalRequest.method,
          status: error.response?.status,
          message: error.message,
        }
      );

      return new Promise((resolve, reject) => {
        setTimeout(() => {
          api(originalRequest)
            .then(resolve)
            .catch((retryError) => {
              // If this was the last retry attempt, log it
              if (originalRequest._retryCount === RETRY_CONFIG.MAX_RETRIES) {
                logger.error(
                  `[API Retry] All retry attempts exhausted for ${originalRequest.url}`,
                  retryError
                );
              }
              reject(retryError);
            });
        }, delay);
      });
    }

    return Promise.reject(error);
  }
);

export { api };
export default api;
